import { createApp } from 'vue';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import FloatingVue from 'floating-vue';
import { createVfm } from 'vue-final-modal';
import VueApexCharts from 'vue3-apexcharts';
import draggable from 'vuedraggable';
import 'vue-select/dist/vue-select.css';
import 'floating-vue/dist/style.css';
import '@/assets/css/theme.css';
import '@/assets/css/tailwind.css';
import '@/assets/css/vue-select.css';
import '@/assets/css/tooltip.css';
import store from '@/plugins/store';
import router from '@/plugins/router';
import modalPlugin from '@/plugins/modal';
import i18n from '@/plugins/i18n';
import PaddleController from '@/helpers/paddle';
import { setStore } from '@/helpers/api';

import App from '@/components/App.vue';
import DefaultLayout from '@/components/layouts/Default';
import DefaultNoHeader from '@/components/layouts/DefaultNoHeader';
import EvaluateItem from '@/components/flow/evaluate/EvaluateItem';

import '@/assets/css/vfm.css';

import OutsideClickDirective from './directives/outsideClickDirective';

import VariableChip from './ui-components/VariableChip';
import VariableSelect from './ui-components/VariableSelect';
import vSelect from './ui-components/VSelectWrapper';
import LinkButton from './ui-components/LinkButton';
import Button from './ui-components/Button';
import PillButton from './ui-components/PillButton';
import IconToggleButton from './ui-components/IconToggleButton';
import Badge from './ui-components/Badge';
import Modal from './ui-components/Modal';
import FullScreenModal from './ui-components/FullScreenModal';
import Avatar from './ui-components/Avatar';
import Input from './ui-components/Input';
import FormInput from './ui-components/FormInput';
import FormTimeInput from './ui-components/FormTimeInput';
import FormTextarea from './ui-components/FormTextarea';
import FormPassword from './ui-components/FormPassword';
import FormSelect from './ui-components/FormSelect';
import FormSelectMultiBadge from './ui-components/FormSelectMultiBadge';
import Textarea from './ui-components/Textarea';
import Toast from './ui-components/Toast';
import Icon from './ui-components/Icon';
import Card from './ui-components/Card';
import MultiSelect from './ui-components/MultiSelect';
import Divider from './ui-components/Divider';
import ListCard from './ui-components/ListCard';
import Spinner from './ui-components/Spinner';
import RadioButton from './ui-components/RadioButton';
import Checkbox from './ui-components/Checkbox';
import ButtonToggle from './ui-components/ButtonToggle';
import CollapseMenu from './ui-components/CollapseMenu';
import CollapseMenuBorderless from './ui-components/CollapseMenuBorderless';
import CollapseCard from './ui-components/CollapseCard';
import ContentCard from './ui-components/ContentCard';
import Toggle from './ui-components/Toggle';
import IconButton from './ui-components/IconButton';
import DataTable from './ui-components/DataTable';
import ValidationObserver from './ui-components/ValidationObserver';
import Notifications from './ui-components/Notifications';
import Quota from './ui-components/Quota';
import Navigation from './ui-components/Navigation';
import BottomNavigation from './ui-components/BottomNavigation';
import ImageCropper from './ui-components/ImageCropper';
import Tabs from './ui-components/Tabs';
import MaterialInput from './ui-components/MaterialInput';
import MaterialSelect from './ui-components/MaterialSelect';
import FormLabel from './ui-components/FormLabel';
import FormError from './ui-components/FormError';
import Alert from './ui-components/Alert';
import ButtonDropdown from './ui-components/ButtonDropdown';
import Swiper from './ui-components/Swiper';
import SwiperSlide from './ui-components/SwiperSlide';
import IntersectionObserver from './ui-components/IntersectionObserver';
import Prompt from './ui-components/Prompt';
import AccordionItem from './ui-components/AccordionItem';
import Accordion from './ui-components/Accordion';
import ExpressionRenderer from './ui-components/ExpressionRenderer';
import PhraseEditor from './ui-components/PhraseEditor';
import ExpressionEditor from './ui-components/ExpressionEditor.vue';
import TableElevated from './ui-components/TableElevated.vue';
import ProjectSelectDropdown from './ui-components/ProjectSelectDropdown.vue';
import DeleteModal from './components/modals/DeleteModal.vue';

import FadeTransition from './components/transitions/FadeTransition.vue';

setStore(store);
router.setStore(store);

const vfm = createVfm();

dayjs.extend(isToday);

// TODO fix this
// Vue.config.ignoredElements = ['g', 'r'];

const app = createApp(App);
app.directive('outside-click', OutsideClickDirective);

app.component('v-select', vSelect);
app.component('apexchart', window.VueApexCharts);
app.component('DefaultLayout', DefaultLayout);
app.component('DefaultNoHeaderLayout', DefaultNoHeader);
app.component('PillButton', PillButton);
app.component('IconToggleButton', IconToggleButton);
app.component('Badge', Badge);
app.component('Avatar', Avatar);
app.component('Input', Input);
app.component('FormInput', FormInput);
app.component('FormTimeInput', FormTimeInput);
app.component('FormTextarea', FormTextarea);
app.component('FormPassword', FormPassword);
app.component('FormSelect', FormSelect);
app.component('FormSelectMultiBadge', FormSelectMultiBadge);
app.component('Textarea', Textarea);
app.component('Button', Button);
app.component('ButtonToggle', ButtonToggle);
app.component('MultiSelect', MultiSelect);
app.component('LinkButton', LinkButton);
app.component('Modal', Modal);
app.component('FullScreenModal', FullScreenModal);
app.component('Toast', Toast);
app.component('Icon', Icon);
app.component('IconButton', IconButton);
app.component('Card', Card);
app.component('Divider', Divider);
app.component('ListCard', ListCard);
app.component('Spinner', Spinner);
app.component('RadioButton', RadioButton);
app.component('Checkbox', Checkbox);
app.component('CollapseMenu', CollapseMenu);
app.component('CollapseMenuBorderless', CollapseMenuBorderless);
app.component('CollapseCard', CollapseCard);
app.component('ContentCard', ContentCard);
app.component('EvaluateItem', EvaluateItem);
app.component('VariableChip', VariableChip);
app.component('VariableSelect', VariableSelect);
app.component('DataTable', DataTable);
app.component('Toggle', Toggle);
app.component('Draggable', draggable);
app.component('ValidationObserver', ValidationObserver);
app.component('Notifications', Notifications);
app.component('Quota', Quota);
app.component('Navigation', Navigation);
app.component('BottomNavigation', BottomNavigation);
app.component('ImageCropper', ImageCropper);
app.component('Tabs', Tabs);
app.component('MaterialInput', MaterialInput);
app.component('MaterialSelect', MaterialSelect);
app.component('FormLabel', FormLabel);
app.component('FormError', FormError);
app.component('Alert', Alert);
app.component('ButtonDropdown', ButtonDropdown);
app.component('Swiper', Swiper);
app.component('SwiperSlide', SwiperSlide);
app.component('IntersectionObserver', IntersectionObserver);
app.component('Prompt', Prompt);
app.component('AccordionItem', AccordionItem);
app.component('Accordion', Accordion);
app.component('ExpressionRenderer', ExpressionRenderer);
app.component('PhraseEditor', PhraseEditor);
app.component('FadeTransition', FadeTransition);
app.component('ExpressionEditor', ExpressionEditor);
app.component('TableElevated', TableElevated);
app.component('ProjectSelectDropdown', ProjectSelectDropdown);
app.component('DeleteModal', DeleteModal);

app.use(store).use(router).use(i18n).use(vfm).mount('#app');
app.use(modalPlugin, { appInstance: app });

// https://floating-vue.starpad.dev/guide/directive
app.use(FloatingVue);
app.use(VueApexCharts);

PaddleController.setup();
